/**
 * Function roles — read model for the merged `/settings/function-roles` page.
 *
 * A function role is a per-org row carrying a label (what people in this role
 * do) AND the agent rule the user's agent loads via MCP for that role. The
 * settings-role-rules page was retired on 2026-05-17 and folded into this one;
 * the `role_rules` table stays 1:1 with `function_roles` and shows up here as
 * the `agentRule` field on each `FunctionRole`.
 *
 * Function role is NOT an authorization axis — that's `permission_role`.
 */

export type FunctionRoleKind = 'system' | 'custom';

export type RoleRuleStatus = 'authored' | 'default' | 'archived';

/** Soft length warning threshold on the agent-rule markdown. No hard cap. */
export const RULE_SOFT_WARN = 4000;

/**
 * Platform-defined fallback agent rule. Used when a function role has no
 * org-authored rule or its rule is archived. Never persisted — code constant.
 */
export const HARDCODED_DEFAULT_RULE =
  'Read the document end-to-end before responding. Cite the document by name when proposing changes. Prefer questions over assumptions when intent is unclear.';

/**
 * One agent-rule snapshot in `role_rule_versions`. Appended on every Save and
 * version-Restore. `restoredFromVersionId` is null on an ordinary snapshot;
 * it points at the source snapshot when a version-Restore produced this row.
 */
export interface RoleRuleVersion {
  id: string;
  roleRuleId: string;
  versionNumber: number;
  content: string;
  restoredFromVersionId: string | null;
  createdAt: string;
  createdBy: string | null;
}

/**
 * The 1:1 agent rule belonging to a function role. `ruleId === null` means no
 * `role_rules` row exists yet — the agent falls back to the hardcoded default
 * until the rule is first authored.
 */
export interface RoleRule {
  ruleId: string | null;
  content: string;
  lastPropagatedAt: string | null;
  archivedAt: string | null;
}

export interface FunctionRole {
  id: string;
  /** Stable slug, immutable after creation. */
  key: string;
  label: string;
  description: string | null;
  kind: FunctionRoleKind;
  /** Active memberships assigned this role in the org. */
  memberCount: number;
  /** The role's 1:1 agent rule (or default when no row exists yet). */
  agentRule: RoleRule;
}

/**
 * The five system role keys, seeded per org on creation. Fixed canonical order
 * for the System section of the role list.
 */
export const SYSTEM_ROLE_KEYS = [
  'engineer',
  'designer',
  'product',
  'qa',
  'other'
] as const;

const SYSTEM_ROLE_ORDER: Record<string, number> = Object.fromEntries(
  SYSTEM_ROLE_KEYS.map((k, i) => [k, i])
);

/**
 * Splits roles into the System section (fixed canonical order) and the Custom
 * section (alphabetical by label, case-insensitive).
 */
export function sortRoles(roles: readonly FunctionRole[]): {
  system: FunctionRole[];
  custom: FunctionRole[];
} {
  const system = roles
    .filter((r) => r.kind === 'system')
    .sort(
      (a, b) =>
        (SYSTEM_ROLE_ORDER[a.key] ?? 99) - (SYSTEM_ROLE_ORDER[b.key] ?? 99)
    );
  const custom = roles
    .filter((r) => r.kind === 'custom')
    .sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  return { system, custom };
}

/**
 * Resolves the MCP status chip for a role's agent rule:
 * - `archived` — `archived_at` is set; agents fall back to the default.
 * - `default`  — no row, empty content, or content == the hardcoded default.
 * - `authored` — an org-defined rule exists.
 */
export function resolveRuleStatus(rule: RoleRule): RoleRuleStatus {
  if (rule.archivedAt) return 'archived';
  const trimmed = rule.content.trim();
  if (trimmed === '' || rule.content === HARDCODED_DEFAULT_RULE) {
    return 'default';
  }
  return 'authored';
}

export function ruleStatusLabel(status: RoleRuleStatus): string {
  if (status === 'authored') return 'Authored';
  if (status === 'archived') return 'Archived';
  return 'Using default';
}

/** Newest first — by version number descending. */
export function sortRoleRuleVersions(
  versions: readonly RoleRuleVersion[]
): RoleRuleVersion[] {
  return [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
}

/** An empty agent rule — used when no `role_rules` row exists yet. */
export function emptyRule(): RoleRule {
  return {
    ruleId: null,
    content: '',
    lastPropagatedAt: null,
    archivedAt: null
  };
}
